import React, { useEffect, useState } from "react";
import { PulsanteFormazione } from "../components/RichiestaFormazione";
import { apriRichiestaAbbonamento } from "../components/RichiestaAbbonamento";
import { collection, onSnapshot, updateDoc, doc, query, where, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { RUOLI_OPERATIVI } from "../utils/permessiDefaultPerRuolo";
import { bloccaSeReadOnly } from "../utils/trialGate";
import { confirm } from "../utils/confirm";
import { statoPiano, avvisoNuovoUtente, preventivo, euro, LISTINO, PIANI_INFO } from "../utils/pianiNexiora";
import { UserCheck, UserX, Shield, User, CreditCard, AlertTriangle, Copy, X, Mail, Clock, Ban } from "lucide-react";
import { statoPiva } from "../utils/pivaOrganizzazione";
import { richiediPiva } from "../components/PivaObbligatoria";
import { statoVerificaEmail } from "../utils/verificaEmail";

function scadenzaFormattata(data) {
  if (!data) return "—";
  const d = data?.toDate ? data.toDate() : new Date(data);
  return d.toLocaleDateString("it-IT");
}
function invitoScaduto(inv) {
  if (!inv.scadenza) return false;
  const d = inv.scadenza?.toDate ? inv.scadenza.toDate() : new Date(inv.scadenza);
  return d < new Date();
}

export default function OrgUsersPage() {
  const { currentUser, userProfile, isOrgAdmin, isTitolare, isPlatformOwner, isReadOnly, creaInvito, revocaInvito, emailVerificata, rimandaVerificaEmail } = useAuth();
  // W445 (regole 72): nominare admin e titolari, invitare un futuro titolare e toccare un
  // titolare spettano solo al titolare; ogni cambio di ruolo o accesso è firmato (ultimaModificaDa)
  // e finisce nel registro modifiche, scritto dal server.
  const puoNominare = isTitolare || isPlatformOwner;
  const firma = { ultimaModificaDa: currentUser?.uid };
  const { effectiveOrgId: organizationId, orgVisitata: organization } = useOrg();
  const { addToast } = useToast();
  const { checkLimit } = useFeatures();
  const [limitError, setLimitError] = useState(null);
  const [users, setUsers] = useState([]);
  const [inviti, setInviti] = useState([]);
  const [sceltaRuoloPer, setSceltaRuoloPer] = useState(null); // utente in attesa di scegliere il ruolo
  const [ruoloScelto, setRuoloScelto] = useState("");
  const [emailInvito, setEmailInvito] = useState("");
  const [ruoloInvito, setRuoloInvito] = useState("");
  const [inviaComeTitolare, setInvitaComeTitolare] = useState(false);
  const [creandoInvito, setCreandoInvito] = useState(false);


  // Assegnare il ruolo operativo (PM, Magazziniere, ecc.) è un compito organizzativo/HR che
  // apriamo anche a Gestione aziendale e Amministrazione. Abilitare/disabilitare un account
  // (tocca i posti a pagamento) e promuovere/rimuovere Admin restano riservati a Titolare/Admin
  // — leve più delicate, con implicazioni di costo o di potere, che teniamo più protette.
  const puoAssegnareRuolo = isOrgAdmin || isPlatformOwner || ["gestione","amministrazione"].includes(userProfile?.ruoloOperativo);

  useEffect(() => {
    if (!organizationId) return;
    // Niente orderBy combinato con where: richiede indice composito e fallisce silenziosamente.
    const q = query(collection(db, "users"), where("organizationId", "==", organizationId));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a,b) => (b.createdAt?.toMillis?.()||0) - (a.createdAt?.toMillis?.()||0));
      setUsers(list);
    });
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    const q = query(collection(db, "inviti"), where("organizationId", "==", organizationId), where("stato", "==", "in_attesa"));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a,b) => (b.createdAt?.toMillis?.()||0) - (a.createdAt?.toMillis?.()||0));
      setInviti(list);
    });
  }, [organizationId]);

  if (!puoAssegnareRuolo) return (
    <div style={{ padding:32 }}>
      <p style={{ color:"#6b7280" }}>Solo Titolare, Admin, Gestione aziendale e Amministrazione possono gestire gli utenti.</p>
    </div>
  );

  const enabledCount = users.filter(u => u.enabled).length;
  // Rev2 (ott 2026): nessun piano gratuito — dopo la prova serve il Pro o il Gold
  // Stato del piano dell'organizzazione in uso (fonte unica: utils/pianiNexiora.js).
  // Il conteggio vero degli utenti attivi è enabledCount (seatsUsed può essere indietro).
  const statoOrg = statoPiano({ ...(organization||{}), seatsUsed: enabledCount }, { isPlatformOwner: false });
  const senzaPianoPagato = !statoOrg.pianoPagato;
  const provaFinita = senzaPianoPagato && !statoOrg.inProva;
  const prevPro = preventivo(Math.max(1, enabledCount), "pro");

  // Prima di abilitare un utente: blocco o avviso secondo il listino.
  //  • Prova finita senza piano → non si abilita nessuno: serve un piano.
  //  • Prova in corso → si abilita; si avvisa quando scatta un blocco di posti in più.
  //  • Piano pagato → oltre i posti acquistati si avvisa del nuovo blocco.
  const aggiornaUtentiAttiviServer = () => {
    try {
      httpsCallable(getFunctions(), "aggiornaUtentiAttivi")({ orgId: organizationId })
        .catch(err => console.warn("Riconteggio utenti attivi non riuscito (verrà ricalcolato stanotte):", err?.message));
    } catch (err) { console.warn(err); }
  };

  const verificaNuovoUtente = async () => {
    if (isPlatformOwner) return true;
    const nuovi = enabledCount + 1;
    const stato = { ...statoOrg, utentiAttivi: nuovi };
    if (!stato.pianoPagato && !stato.inProva) {
      const p = preventivo(nuovi, "pro");
      addToast(`La prova è terminata: per abilitare utenti attiva il piano Pro (${p.posti} posti, ${euro(p.annuale)} all'anno IVA esclusa) o Gold con «Attiva il piano».`, "error");
      return false;
    }
    const msg = avvisoNuovoUtente(stato, nuovi);
    if (msg && !await confirm(msg + " Confermi di voler abilitare questo utente?", { type:"warning" })) return false;
    return true;
  };

  const toggle = async u => {
    if (!isOrgAdmin && !isPlatformOwner) { addToast("Solo Titolare o Admin possono abilitare/disabilitare un account — tocca i posti a pagamento","error"); return; }
    // In sola lettura (prova finita, nessun piano) DISATTIVARE resta sempre possibile.
    if (!u.enabled && bloccaSeReadOnly(isReadOnly, addToast)) return;
    if (!u.enabled && !await verificaNuovoUtente()) return;
    // Abilitare un utente senza ruolo operativo richiede prima di sceglierlo — obbligatorio,
    // non un dettaglio facoltativo: da quel ruolo dipendono i permessi di default.
    if (!u.enabled && !u.ruoloOperativo) {
      setSceltaRuoloPer(u);
      setRuoloScelto("");
      return;
    }
    await updateDoc(doc(db, "users", u.uid), { enabled: !u.enabled, ...firma });
    addToast(u.enabled ? `${u.displayName} disabilitato` : `${u.displayName} abilitato!`, u.enabled ? "info" : "success");

    // Utenti attivi (seatsUsed): li ricalcola il server. È un campo di fatturazione che le
    // Regole Firestore non lasciano scrivere ai clienti — la vecchia scrittura diretta da qui
    // veniva sempre rifiutata e il conteggio restava fermo.
    aggiornaUtentiAttiviServer();
  };

  const confermaAbilitazioneConRuolo = async () => {
    if (!ruoloScelto) { addToast("Seleziona un ruolo","error"); return; }
    const u = sceltaRuoloPer;
    await updateDoc(doc(db, "users", u.uid), { enabled: true, ruoloOperativo: ruoloScelto, ...firma });
    addToast(`${u.displayName} abilitato come ${RUOLI_OPERATIVI.find(r=>r.id===ruoloScelto)?.label} ✅`,"success");
    aggiornaUtentiAttiviServer();
    setSceltaRuoloPer(null);
  };

  const cambiaRuoloOperativo = async (u, ruolo) => {
    await updateDoc(doc(db, "users", u.uid), { ruoloOperativo: ruolo, ...firma });
    addToast("Ruolo aggiornato ✅","success");
  };

  const toggleRole = async u => {
    if (!puoNominare) { addToast("Solo il Titolare nomina o toglie gli amministratori","error"); return; }
    if (u.uid === currentUser.uid) { addToast("Non puoi modificare il tuo ruolo", "error"); return; }
    if (u.role === "titolare") { addToast("Il titolare non può essere declassato da qui — usa \"Rimuovi ruolo Titolare\"","error"); return; }
    await updateDoc(doc(db, "users", u.uid), { role: u.role === "admin" ? "member" : "admin", ...firma });
    addToast("Ruolo aggiornato", "info");
  };

  // Promuove un membro a Titolare — non "ruba" nulla a chi lo è già: possono coesistere più
  // Titolari nella stessa organizzazione. Utile in particolare quando l'organizzazione è
  // stata registrata inizialmente da un responsabile (non il vero titolare, magari perché
  // era l'unico contatto disponibile al momento della presentazione) — appena il vero
  // titolare ha un account, gli si dà il ruolo corretto con un click, senza dover
  // "retrocedere" nessuno prima.
  const promuoviATitolare = async u => {
    if (!puoNominare) { addToast("Solo il Titolare può nominare un altro Titolare","error"); return; }
    if (u.role === "titolare") return;
    if (!await confirm(`Promuovere ${u.displayName} a Titolare? Avrà accesso pieno e amministrativo, permanente.`, { type:"warning" })) return;
    await updateDoc(doc(db, "users", u.uid), { role: "titolare", ...firma });
    addToast(`${u.displayName} è ora Titolare`, "success");
  };

  // Toglie il ruolo di Titolare — diventa Admin, mantenendo comunque ampi permessi (non
  // torna un membro qualsiasi). Non permesso su se stessi (evita di togliersi l'accesso
  // per errore), e mai sull'ultimo Titolare rimasto: un'organizzazione senza nessun
  // Titolare resterebbe senza nessuno con davvero pieni poteri su di essa.
  const rimuoviTitolare = async u => {
    if (!puoNominare) { addToast("Solo il Titolare può modificare questo ruolo","error"); return; }
    if (u.uid === currentUser.uid) { addToast("Non puoi rimuovere il tuo stesso ruolo di Titolare","error"); return; }
    const numTitolari = users.filter(x => x.role === "titolare").length;
    if (numTitolari <= 1) { addToast("Non puoi rimuovere l'unico Titolare rimasto — l'organizzazione ne deve avere sempre almeno uno","error"); return; }
    if (!await confirm(`Rimuovere il ruolo di Titolare a ${u.displayName}? Diventerà Admin, mantenendo comunque ampi permessi.`, { type:"danger" })) return;
    await updateDoc(doc(db, "users", u.uid), { role: "admin", ...firma });
    addToast(`${u.displayName} non è più Titolare (ora Admin)`, "info");
  };

  const updateDept = async (uid, dept) => await updateDoc(doc(db, "users", uid), { department: dept });


  const inviaInvito = async () => {
    if (bloccaSeReadOnly(isReadOnly, addToast)) return;
    // Prima di invitare il primo collega serve la Partita IVA dell'azienda (utils/pivaOrganizzazione.js)
    if (statoPiva(organization).mancante && !isPlatformOwner) {
      richiediPiva("Per invitare i colleghi serve prima la Partita IVA dell'azienda.");
      return;
    }
    // W446: per invitare serve l'email confermata (aziende registrate dal W446, Regole 73)
    if (!isPlatformOwner && statoVerificaEmail({ org: organization, emailVerificata }).richiesta) {
      if (await confirm(`Per invitare i colleghi conferma prima la tua email (${currentUser?.email}) con il link che ti abbiamo mandato. Vuoi che te lo rimandiamo?`, { title: "Conferma la tua email", type: "info", confirmLabel: "Sì, rimandala", cancelLabel: "Non ora" })) {
        try { await rimandaVerificaEmail(); addToast("Email inviata: apri il link «Conferma la mia email», poi torna qui.", "success", 8000); }
        catch (e) { addToast(e?.message && !/internal/i.test(e.message) ? e.message : "Invio non riuscito: riprova tra qualche minuto.", "error"); }
      }
      return;
    }
    const email = emailInvito.trim().toLowerCase();
    if (!email || !email.includes("@")) { addToast("Inserisci un'email valida", "error"); return; }
    if (users.some(u => u.email?.toLowerCase() === email)) { addToast("Questa persona è già membro dell'organizzazione", "error"); return; }
    if (inviti.some(i => i.emailDestinatario === email)) { addToast("C'è già un invito in attesa per questa email", "error"); return; }
    if (!ruoloInvito && !inviaComeTitolare) { addToast("Scegli il ruolo: con l'invito accettato la persona entra subito con quel ruolo", "error"); return; }
    if (inviaComeTitolare && !puoNominare) { addToast("Solo il Titolare può invitare un futuro Titolare", "error"); return; }
    // Accettato l'invito si entra subito: l'avviso sui posti a pagamento arriva adesso
    if (!await verificaNuovoUtente()) return;
    if (inviaComeTitolare && !await confirm(`Confermi che ${email} diventerà Titolare, con accesso pieno e amministrativo, appena accetta l'invito?`, { type:"warning" })) return;
    setCreandoInvito(true);
    try {
      const token = await creaInvito({ email, ruoloPrevisto: inviaComeTitolare ? null : (ruoloInvito || null), diventaTitolare: inviaComeTitolare, organizationId, organizationName: organization?.name || organization?.nome || "" });
      navigator.clipboard.writeText(token);
      // L'email è un vantaggio in più, non deve mai bloccare l'invito: il codice è già
      // valido a prescindere, quindi se l'invio fallisce (rete, SendGrid, ecc.) chi ha
      // invitato ha comunque il codice pronto negli appunti da condividere a mano.
      try {
        await httpsCallable(getFunctions(), "inviaEmailInvito")({ inviteId: token });
        addToast(inviaComeTitolare ? "Invito da Titolare inviato via email — codice copiato anche negli appunti" : "Invito inviato via email — codice copiato anche negli appunti", "success");
      } catch (errEmail) {
        addToast("Invito creato e codice copiato, ma l'email non è partita — condividi il codice a mano", "info");
      }
      setEmailInvito(""); setRuoloInvito(""); setInvitaComeTitolare(false);
    } catch (err) { addToast(`Errore nella creazione dell'invito: ${err.message || err}`, "error"); }
    setCreandoInvito(false);
  };

  const revoca = async (inv) => {
    if (!await confirm(`Revocare l'invito per ${inv.emailDestinatario}?`, { type: "danger" })) return;
    await revocaInvito(inv.id);
    addToast("Invito revocato", "info");
  };

  return (
    <div style={{ padding:24, maxWidth:900, flex:1, overflowY:"auto" }}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>Gestione Utenti</h2>
          <p style={{ fontSize:13, color:"#6b7280" }}>{enabledCount} utenti attivi · {users.filter(u=>!u.enabled).length} in attesa</p>
        </div>
        {/* Invita per email — percorso consigliato: tracciato, con scadenza, legato a
            un'unica email destinataria (verificata al momento della registrazione). */}
        {(isOrgAdmin||isPlatformOwner) && (
        <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", minWidth:280 }}>
          <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:6, display:"flex", alignItems:"center", gap:5 }}>
            <Mail size={12}/> Invita per email
          </div>
          <div style={{ display:"flex", gap:6, marginBottom:6 }}>
            <input value={emailInvito} onChange={e=>setEmailInvito(e.target.value)} placeholder="nome@azienda.it"
              style={{flex:1,border:"1px solid #e5e7eb",borderRadius:6,padding:"6px 8px",fontSize:12,fontFamily:"'Inter',sans-serif"}}/>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <select value={ruoloInvito} onChange={e=>setRuoloInvito(e.target.value)}
              style={{flex:1,border:"1px solid #e5e7eb",borderRadius:6,padding:"6px 8px",fontSize:11.5,fontFamily:"'Inter',sans-serif"}}>
              <option value="">Ruolo (obbligatorio)</option>
              {RUOLI_OPERATIVI.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <button onClick={inviaInvito} disabled={creandoInvito}
              style={{padding:"6px 14px",border:"none",borderRadius:7,background:"#7c3aed",color:"#fff",fontSize:12,fontWeight:700,cursor:creandoInvito?"default":"pointer",opacity:creandoInvito?.6:1,fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
              {creandoInvito?"...":"Invita"}
            </button>
          </div>
          {puoNominare && (
            <label style={{ display:"flex", alignItems:"center", gap:6, marginTop:8, fontSize:11, color:"#374151", cursor:"pointer" }}>
              <input type="checkbox" checked={inviaComeTitolare} onChange={e=>setInvitaComeTitolare(e.target.checked)}/>
              Questa persona diventerà Titolare — accesso pieno fin da subito, nessuna approvazione richiesta
            </label>
          )}
          <div style={{ fontSize:10, color:"#9ca3af", marginTop:6, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span>Con l'invito accettato entra subito con il ruolo scelto · valido 7 giorni</span>
          </div>
        </div>
        )}
      </div>

      {/* Inviti in attesa */}
      {inviti.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#6b7280", marginBottom:8, display:"flex", alignItems:"center", gap:5 }}>
            <Clock size={13}/> Inviti in attesa ({inviti.length})
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {inviti.map(inv => (
              <div key={inv.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, fontSize:12 }}>
                <Mail size={13} color="#7c3aed"/>
                <span style={{flex:1,fontWeight:600,color:"#374151"}}>{inv.emailDestinatario}</span>
                {inv.ruoloPrevisto && <span style={{fontSize:10,color:"#7c3aed"}}>{RUOLI_OPERATIVI.find(r=>r.id===inv.ruoloPrevisto)?.label}</span>}
                <span style={{fontSize:10,color: invitoScaduto(inv) ? "#dc2626" : "#9ca3af"}}>
                  {invitoScaduto(inv) ? "Scaduto" : `Scade il ${scadenzaFormattata(inv.scadenza)}`}
                </span>
                <button onClick={()=>revoca(inv)} title="Revoca invito"
                  style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",display:"flex",alignItems:"center",gap:3,fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                  <Ban size={12}/> Revoca
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Banner piano — testi e importi dal listino (utils/pianiNexiora.js) */}
      {(() => {
        const inProva = statoOrg.inProva;
        const pagato = statoOrg.pianoPagato;
        const colore = pagato ? "#166534" : "#92400e";
        const titolo = pagato
          ? `Piano ${PIANI_INFO[pagato].label} — ${enabledCount} utenti attivi${statoOrg.postiAcquistati ? ` su ${statoOrg.postiAcquistati} posti` : ""}`
          : inProva
            ? `Prova gratuita — ${enabledCount} utenti attivi (tutti i moduli inclusi)`
            : `Prova terminata — ${enabledCount} utenti attivi, nessun piano attivo`;
        const testo = pagato
          ? `Oltre i posti acquistati si aggiunge un blocco di ${LISTINO.bloccoPosti} posti, pagato in proporzione ai giorni che restano fino al rinnovo annuale.`
          : inProva
            ? `Alla fine della prova serve il piano Pro o Gold. Con ${Math.max(1, enabledCount)} utenti il Pro costa ${euro(prevPro.mensile)}/mese IVA esclusa (${prevPro.posti} posti), il Gold ${euro(preventivo(Math.max(1, enabledCount), "gold").mensile)}/mese.`
            : `Nexiora è in sola lettura. Con ${Math.max(1, enabledCount)} utenti il piano Pro costa ${euro(prevPro.annuale)} all'anno IVA esclusa (${prevPro.posti} posti, ${euro(prevPro.mensile)}/mese di listino con lo sconto annuale): attivalo da qui.`;
        return (
          <div style={{ background: pagato ? "#f0fdf4" : provaFinita ? "#fff7ed" : "#fffbeb", border:`1px solid ${pagato?"#bbf7d0":provaFinita?"#fed7aa":"#fde68a"}`, borderRadius:10, padding:"14px 18px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
            {provaFinita ? <AlertTriangle size={18} color="#d97706"/> : <CreditCard size={18} color={pagato?"#059669":"#d97706"}/>}
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600, color: colore }}>{titolo}</div>
              <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>{testo}</div>
            </div>
            {!pagato && (
              <button type="button" onClick={apriRichiestaAbbonamento}
                style={{ background:"#7c3aed", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif", whiteSpace:"nowrap" }}>
                Attiva il piano →
              </button>
            )}
            {/* W426 */}
            <PulsanteFormazione stile={{ whiteSpace:"nowrap" }}/>
          </div>
        );
      })()}

      {/* Scelta obbligatoria del ruolo — prima di poter abilitare un utente senza ruolo */}
      {sceltaRuoloPer && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:14,padding:22,maxWidth:420,width:"90%"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <h3 style={{fontSize:15,fontWeight:800,color:"#1e1b4b"}}>Scegli il ruolo di {sceltaRuoloPer.displayName}</h3>
              <button onClick={()=>setSceltaRuoloPer(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={18}/></button>
            </div>
            <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Il ruolo determina cosa vede subito questa persona in Nexiora — potrai comunque personalizzare i permessi dopo, per ruolo o per singola persona, in Abilitazioni.</p>
            {RUOLI_OPERATIVI.map(r => (
              <button key={r.id} onClick={()=>setRuoloScelto(r.id)}
                style={{width:"100%",textAlign:"left",padding:"10px 12px",borderRadius:9,border:`1.5px solid ${ruoloScelto===r.id?"#7c3aed":"#e5e7eb"}`,background:ruoloScelto===r.id?"#f5f3ff":"#fff",cursor:"pointer",marginBottom:6,fontFamily:"'Inter',sans-serif"}}>
                <div style={{fontSize:13,fontWeight:700,color:ruoloScelto===r.id?"#7c3aed":"#111827"}}>{r.label}</div>
                <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{r.descrizione}</div>
              </button>
            ))}
            <button onClick={confermaAbilitazioneConRuolo} disabled={!ruoloScelto}
              style={{width:"100%",marginTop:10,padding:"9px",borderRadius:9,border:"none",background:ruoloScelto?"#7c3aed":"#e5e7eb",color:ruoloScelto?"#fff":"#9ca3af",fontSize:13,fontWeight:700,cursor:ruoloScelto?"pointer":"default"}}>
              Abilita con questo ruolo
            </button>
          </div>
        </div>
      )}

      {/* Lista utenti */}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {users.map(u => (
          <div key={u.uid} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:"#fff", border:`1px solid ${!u.enabled?"#fef3c7":"#e5e7eb"}`, borderRadius:10, flexWrap:"wrap" }}>
            <div style={{ width:38, height:38, borderRadius:"50%", background: u.role==="titolare"?"#d97706":u.role==="admin"?"#7c3aed":"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:15, color:"#fff", border:`2px solid ${u.enabled?"#059669":"#d97706"}`, flexShrink:0 }}>
              {u.displayName?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:140 }}>
              <div style={{ fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                {u.displayName}
                {u.role === "titolare" && <span style={{ fontSize:10, background:"#fef3c7", color:"#d97706", padding:"1px 6px", borderRadius:4, fontWeight:700 }}>TITOLARE</span>}
                {u.role === "admin" && <span style={{ fontSize:10, background:"#ede9fe", color:"#7c3aed", padding:"1px 6px", borderRadius:4, fontWeight:700 }}>ADMIN</span>}
                {!u.enabled && <span style={{ fontSize:10, background:"#fef3c7", color:"#d97706", padding:"1px 6px", borderRadius:4, fontWeight:700 }}>IN ATTESA</span>}
                {u.uid === currentUser.uid && <span style={{ fontSize:10, background:"#f3f4f6", color:"#6b7280", padding:"1px 6px", borderRadius:4 }}>TU</span>}
              </div>
              <div style={{ fontSize:12, color:"#9ca3af", display:"flex", alignItems:"center", gap:6 }}>
                {u.email}
                {organization?.dominioAziendale && u.email?.toLowerCase().endsWith("@"+organization.dominioAziendale) ? (
                  <span style={{fontSize:9,color:"#059669"}}>· email aziendale</span>
                ) : (
                  <span style={{fontSize:9,color:"#9ca3af"}}>· invitato</span>
                )}
              </div>
            </div>
            <select value={u.ruoloOperativo||""} onChange={e=>cambiaRuoloOperativo(u,e.target.value)}
              style={{border:"1px solid #e5e7eb",borderRadius:6,padding:"5px 8px",fontSize:11.5,fontFamily:"'Inter',sans-serif",color: u.ruoloOperativo?"#374151":"#d97706",cursor:"pointer"}}>
              <option value="">Ruolo non assegnato</option>
              {RUOLI_OPERATIVI.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <input defaultValue={u.department || ""} onBlur={e => updateDept(u.uid, e.target.value)}
              placeholder="Reparto" style={{ border:"1px solid #e5e7eb", borderRadius:6, padding:"5px 10px", fontSize:12, fontFamily:"'Inter',sans-serif", width:140 }}/>
            <div style={{ display:"flex", gap:6 }}>
              {(isOrgAdmin||isPlatformOwner) && (puoNominare || (u.role !== "titolare" && u.role !== "admin")) && u.uid !== currentUser.uid && (
                <button onClick={() => toggle(u)} className="btn btn-secondary btn-sm"
                  style={!u.enabled ? { color:"#059669", borderColor:"#059669" } : { color:"#dc2626", borderColor:"#dc2626" }}>
                  {u.enabled ? <><UserX size={13}/> Disabilita</> : <><UserCheck size={13}/> Abilita</>}
                </button>
              )}
              {puoNominare && u.uid !== currentUser.uid && u.role !== "titolare" && (
                <button onClick={() => toggleRole(u)} className="btn btn-secondary btn-sm">
                  {u.role === "admin" ? <><User size={13}/> Rimuovi admin</> : <><Shield size={13}/> Rendi admin</>}
                </button>
              )}
              {puoNominare && u.role !== "titolare" && (
                <button onClick={() => promuoviATitolare(u)} className="btn btn-secondary btn-sm" title="Promuove a Titolare — accesso pieno e amministrativo, permanente">
                  <Shield size={13}/> Promuovi a Titolare
                </button>
              )}
              {puoNominare && u.role === "titolare" && u.uid !== currentUser.uid && (
                <button onClick={() => rimuoviTitolare(u)} className="btn btn-secondary btn-sm" title="Rimuove il ruolo di Titolare — diventa Admin">
                  <User size={13}/> Rimuovi ruolo Titolare
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
